// The tiles on the "choose how you want to order" step, and what each one means
// downstream.
//
// Three of the four tiles map straight onto an order type the rest of the page
// already understands. The fourth — THEATER_TILE — is a theater pre-order, and
// it deliberately maps onto 'take-away' internally: a pre-order is picked up at
// the venue, carries no table, pays the same fees and hides the same
// `HideOnTakeOut` items as any other take-away order. Giving it its own order
// type would have meant teaching every one of those rules about a fourth value.
// What separates it is not where the food goes but WHEN — so it travels as a
// tile id plus the `isTheaterPreorder` flag, and only the handful of places
// that care about timing look at that flag.

import translations from '../constants/translations.json';
import { LANGUAGE_LOCALES } from './theater';

export const THEATER_TILE = 'theater';

/*
 * Tickets are a FIFTH tile and, unlike the theater one, they do not map onto an
 * order type at all. A ticket purchase is its own order with its own checkout
 * (see the backend's controller/ticketCheckout.js) — no table, no pickup slot,
 * no menu. So this tile does not enter the ordering flow: it leaves for the
 * ticket page. Everything downstream that reasons about order types therefore
 * never sees it, which is exactly what we want.
 */
export const TICKETS_TILE = 'tickets';

// The order type the rest of the page (fees, category filters, POS payload)
// should see for a tile.
export const orderTypeForTile = (tile) => (tile === THEATER_TILE ? 'take-away' : tile);

/**
 * Does this venue sell to guests at all?
 *
 * "Dine in or take away" was the whole answer until theater mode: a venue with
 * both switched off had nothing to offer, so the page shut every ordering door
 * — the menu items, the basket button, the venue card. A theater that sells
 * ONLY pre-orders is exactly that shape on paper and nothing like it in
 * practice, and it was left looking closed with a full programme on sale.
 *
 * The pre-order tile is built from TheaterModeEnabled alone (buildOrderTypeOptions),
 * never from the two immediate flags, so it is a third way to sell and belongs
 * in this answer. Both spellings are accepted because the list and favourites
 * endpoints send `theaterModeEnabled` while /restaurant/fetchspecific sends
 * `TheaterModeEnabled`.
 *
 * Wolt delivery is a way to sell too: a delivery-only venue gets the one
 * delivery tile, and the app orders from it (hey_waitr orderModes.acceptsOrders).
 */
export const venueSellsOnline = (restaurant) =>
  Boolean(
    restaurant &&
      (restaurant.isdinein ||
        restaurant.istakeout ||
        restaurant.EnableWoltDelivery ||
        restaurant.enableWoltDelivery ||
        restaurant.TheaterModeEnabled ||
        restaurant.theaterModeEnabled ||
        // A venue selling ONLY tickets is in exactly the shape theater mode was
        // in before the fix above: nothing on the two immediate flags, a full
        // programme on sale, and the page shutting every door anyway.
        restaurant.TicketingEnabled ||
        restaurant.ticketingEnabled),
  );

// The tiles that mean "I want this now", in display order.
const immediateTiles = (restaurant) => {
  const tiles = [];
  if (restaurant?.isdinein) tiles.push('dine-in');
  if (restaurant?.istakeout) tiles.push('take-away');
  if (restaurant?.EnableWoltDelivery) tiles.push('delivery');
  return tiles;
};

// The venue's own wording for a tile, set per language in admin (Restaurant →
// Order type labels). The venue chose that word on purpose, so it beats both
// the translation and the theater "Bestil nu" rename. A language left blank
// falls back to the standard translation for that language — never to another
// language's custom text. A plain string (early staging data) covers every
// language.
const CUSTOM_LABEL_KEYS = { 'dine-in': 'dineIn', 'take-away': 'takeAway', delivery: 'delivery' };

export const venueOrderTypeLabel = (tile, language, restaurant) => {
  const key = CUSTOM_LABEL_KEYS[tile];
  const given = key && restaurant?.OrderTypeLabels?.[key];
  const label = typeof given === 'string' ? given : given?.[language];
  return typeof label === 'string' && label.trim() ? label.trim() : '';
};

const labelFor = (tile, language, restaurant) => {
  const custom = venueOrderTypeLabel(tile, language, restaurant);
  if (custom) return custom;

  const t = translations[language] || {};

  // On a theater the choice is about TIME — now, or for a performance — so the
  // one immediate tile is named for that rather than for where the food goes.
  // "Afhent" against "Forudbestilling" puts them on different axes and reads as
  // if the pre-order could not also be collected. Only when the tile stands
  // alone: a venue offering two immediate ways still has to let the guest tell
  // them apart, and two cards both saying "Bestil nu" would not.
  if (
    restaurant?.TheaterModeEnabled &&
    tile !== THEATER_TILE &&
    tile !== TICKETS_TILE &&
    immediateTiles(restaurant).length === 1
  ) {
    return t.order_type_now || 'Bestil nu';
  }

  switch (tile) {
    case TICKETS_TILE:
      return t.order_type_tickets || 'Billetter';
    case 'dine-in':
      return t.orders_dine_in;
    case 'take-away':
      return t.orders_take_away;
    case 'delivery':
      return t.orders_delivery;
    case THEATER_TILE:
      return t.order_type_theater || 'Forudbestilling';
    default:
      return '';
  }
};

/**
 * The tiles a venue offers, in display order.
 *
 * The pre-order tile is included for every theater venue at this point even
 * though the programme has not loaded yet — withTheaterAvailability drops it
 * again once we know the venue has nothing on sale. Building it optimistically
 * keeps the tile from popping in a beat after the page settles.
 *
 * @param {Object} restaurant - processed restaurant from /restaurant/fetch
 * @param {string} language
 * @returns {Array<{id: string, label: string}>}
 */
export const buildOrderTypeOptions = (restaurant, language) => {
  if (!restaurant) return [];

  const tiles = immediateTiles(restaurant);
  if (restaurant.TheaterModeEnabled) tiles.push(THEATER_TILE);
  if (restaurant.TicketingEnabled || restaurant.ticketingEnabled) tiles.push(TICKETS_TILE);

  return tiles.map((id) => ({ id, label: labelFor(id, language, restaurant) }));
};

/** Re-label an existing tile list after a language switch, ids untouched. */
export const translateOrderTypeOptions = (options, language, restaurant) =>
  (options || []).map((option) => ({
    ...option,
    label: labelFor(option.id, language, restaurant) || option.label,
  }));

/**
 * What to do with the pre-order tile when there is nothing on sale.
 *
 * A tile that leads to an empty picker is a dead end, so the original rule was
 * simply to drop it. At a venue that sells ONLY pre-orders that turned the
 * whole page into a dead end instead: no tiles at all, and a guest staring at
 * a blank "choose an option" screen on a day the theatre is playing tomorrow.
 *
 * Two different situations were being treated as one:
 *
 *   1. Nothing on sale YET — the venue's booking window has not reached the
 *      next performance. The tile stays, greyed, naming the day sales open,
 *      exactly as a closed dine-in tile says "Opens today at 16:00". The guest
 *      learns the venue does this and when to come back.
 *   2. Nothing on sale AT ALL — the programme has run out. Now the tile really
 *      is a dead end and goes, and the empty state on the step explains it.
 *
 * `opensDate` ("YYYY-MM-DD" or null) is what separates them, and it comes from
 * the server for the same reason the programme does: only it knows the booking
 * window. An older server that does not send it leaves opensDate null, which
 * lands on the previous behaviour — the tile is dropped — so nothing breaks
 * while the two deploys are out of step.
 *
 * Judged only once the programme has actually been fetched; an initial empty
 * list would otherwise hide the tile on every first render.
 */
export const withTheaterAvailability = (
  options,
  { hasFetchedPerformances, performanceCount, opensDate, language },
) => {
  if (!hasFetchedPerformances || performanceCount > 0) return options || [];

  if (!opensDate) return (options || []).filter((option) => option.id !== THEATER_TILE);

  const note = preorderOpensNote(opensDate, language);

  return (options || []).map((option) =>
    option.id === THEATER_TILE ? { ...option, disabled: true, note } : option,
  );
};

/**
 * "Opens 05/01", or "Opens today"/"Opens tomorrow" when that is what the date
 * means — a guest reading a date on the very day sales open has to do
 * arithmetic to learn they can order in a minute.
 *
 * Each of the three is its OWN phrase per language rather than a date slotted
 * into one template: Spanish needs "Abre el 05/01" but "Abre hoy", and
 * substituting "hoy" into the first gives "Abre el hoy". The day itself is
 * left to the locale, which writes it the way that language writes dates
 * (da "05.01", en-GB "05/01").
 */
const preorderOpensNote = (ymd, language) => {
  const t = translations[language] || {};
  const opens = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(opens.getTime())) return '';

  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const dayDelta = Math.round((opens - today) / 86400000);
  if (dayDelta <= 0) return t.order_type_preorder_opens_today || 'Åbner i dag';
  if (dayDelta === 1) return t.order_type_preorder_opens_tomorrow || 'Åbner i morgen';

  const date = opens.toLocaleDateString(LANGUAGE_LOCALES[language] || 'da-DK', {
    day: '2-digit',
    month: '2-digit',
  });
  return (t.order_type_preorder_opens || 'Åbner {date}').replace('{date}', date);
};

const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The weekday in the guest's own language. Taken from a real upcoming date so
// the locale supplies the word, rather than shipping seven names per language.
const dayName = (dayKey, language) => {
  const index = DAY_KEYS.indexOf(dayKey);
  if (index === -1) return dayKey;

  const date = new Date();
  date.setDate(date.getDate() + ((index - date.getDay() + 7) % 7 || 7));
  return date.toLocaleDateString(LANGUAGE_LOCALES[language] || 'da-DK', { weekday: 'long' });
};

/**
 * Grey out the tiles the venue cannot honour right now.
 *
 * Only the immediate tiles follow opening hours. The pre-order tile is exempt
 * by design — selling tomorrow's show at 09:00 is the whole point of theater
 * mode — and an advance take-away tile is exempt for the same reason, since the
 * guest picks a future slot rather than ordering for now.
 *
 * The closed tiles stay visible rather than disappearing: a guest who cannot
 * see that "Bestil nu" exists has no way to know the venue sells to walk-ins at
 * all, and "Åbner i dag kl. 16:00" answers the obvious next question.
 *
 * @param {Array} options - tiles from buildOrderTypeOptions
 * @param {Object} ctx
 * @param {boolean} ctx.isOpenNow
 * @param {Object} ctx.restaurant
 * @param {string} ctx.language
 * @param {{dayOffset: number, dayKey: string, start: string}|null} ctx.nextOpening
 */
export const withClosedState = (options, { isOpenNow, restaurant, language, nextOpening }) => {
  // Theater venues only. Everywhere else the existing flows already handle a
  // closed venue, and greying a tile here took away advance delivery — which
  // toggleOrderIfNotAllowed and the server both still permit.
  if (isOpenNow || !restaurant?.TheaterModeEnabled) return options || [];

  const t = translations[language] || {};

  const note = () => {
    if (!nextOpening) return t.order_type_closed_now || 'Lukket lige nu';
    if (nextOpening.dayOffset === 0) {
      return (t.order_type_opens_today || 'Åbner i dag kl. {time}').replace(
        '{time}',
        nextOpening.start,
      );
    }
    if (nextOpening.dayOffset === 1) {
      return (t.order_type_opens_tomorrow || 'Åbner i morgen kl. {time}').replace(
        '{time}',
        nextOpening.start,
      );
    }
    return (t.order_type_opens_day || 'Åbner {day} kl. {time}')
      .replace('{day}', dayName(nextOpening.dayKey, language))
      .replace('{time}', nextOpening.start);
  };

  return (options || []).map((option) => {
    if (option.id === THEATER_TILE) return option;
    // Tickets are exempt for the same reason a pre-order is: selling tonight's
    // show at 09:00 is the point, and the sales window on the event decides
    // when it stops — not whether the kitchen is open right now.
    if (option.id === TICKETS_TILE) return option;
    return { ...option, disabled: true, note: note() };
  });
};

/**
 * Is this an ORDER NOW at a theater — the flow that names a performance purely
 * as context for the kitchen, in place of a table number?
 *
 * @param {Object} restaurant
 * @param {boolean} isTheaterPreorder the guest chose the pre-order tile
 * @param {boolean} isDineIn
 */
export const isTheaterOrderNowFlow = (restaurant, isTheaterPreorder, orderType) =>
  Boolean(restaurant && restaurant.TheaterModeEnabled) &&
  !isTheaterPreorder &&
  // Delivery is excluded: a courier dropoff has nothing to do with a
  // performance, and a guest at home is not in any house.
  orderType !== 'delivery';

/** Does this order carry a performance to the server at all? */
export const sendsTheaterPerformance = ({
  isTheaterPreorder,
  isTheaterOrderNow,
  isDineIn,
}) => (Boolean(isTheaterPreorder) && !isDineIn) || Boolean(isTheaterOrderNow);

/**
 * The two theater keys on the checkout payload.
 *
 * `theaterOrderNow` is the whole contract: without it the server reads any
 * performance as a pre-order, pins the order to the showtime and holds its
 * ticket until curtain — which would leave a guest standing at the counter
 * unable to collect what they just bought. Omitting the flag therefore has to
 * mean "pre-order", so every client written before it existed keeps working.
 *
 * Both keys are `undefined` (and so absent from the JSON) on every non-theater
 * order.
 *
 * Lives here rather than inline at the three call sites for a second reason:
 * RestaurantMenu is ~4,000 lines and eslint-plugin-react-hooks 4.3.0 gives up
 * analysing it once the component body carries a few more branches, then
 * reports every hook below ~line 2500 as conditional and fails the build. See
 * the note in utils/stampcard.js. Keeping decisions like this one out of the
 * component keeps that budget in hand.
 */
export const theaterOrderFields = ({
  isTheaterPreorder,
  isTheaterOrderNow,
  isDineIn,
  theaterPerformanceId,
}) => {
  if (!sendsTheaterPerformance({ isTheaterPreorder, isTheaterOrderNow, isDineIn })) {
    return { theaterPerformanceId: undefined, theaterOrderNow: undefined };
  }

  return {
    theaterPerformanceId: theaterPerformanceId || undefined,
    theaterOrderNow: isTheaterOrderNow ? true : undefined,
  };
};

// What the basket shows when the venue has set no bag price of its own.
export const DEFAULT_BAG_FEE = 4;

/**
 * The carrier-bag charge an order payload sends (`bagtakeawayprice`).
 *
 * A delivery ALWAYS carries a bag — the courier needs one, and the basket's bag
 * toggle is locked on for delivery — at the venue's price, or the default when
 * it has none. Before this the payload only sent a bag when the venue had
 * BagTakeAway switched on, so at every other venue the basket showed
 * "Bærepose 1 x 4" and a total the guest was never charged.
 *
 * Take-away keeps the venue's opt-in: a bag only where BagTakeAway is on and
 * the guest said yes. Dine-in never has one.
 */
export const bagTakeAwayPriceFor = ({ restaurant, orderType, isDineIn, hasBag }) => {
  if (isDineIn) return 0;
  if (orderType === 'delivery') return Number(restaurant?.BagTakeAwayPrice) || DEFAULT_BAG_FEE;
  return restaurant?.BagTakeAway && hasBag ? restaurant?.BagTakeAwayPrice || 0 : 0;
};
